import userService from "@/services/user";
import {
  ACCOUNTS_KEY,
  TOKEN_KEY,
  activateAccount,
  expireAccount,
  forgetAccount,
  forgetAllAccounts,
  getActiveToken,
  readAccounts,
  rememberAccount,
  stopAddingAccount,
} from "@/utils/accounts";

// Sačuvani nalozi za prebacivanje bez ponovnog unosa lozinke (vidi
// utils/accounts.js). Lista živi u localStorage, ovdje je samo reaktivna kopija.
const state = () => ({
  list: [],
});

const mutations = {
  SET_ACCOUNTS(state, accounts) {
    state.list = accounts;
  },
};

const actions = {
  // Provjeri token sačuvanog naloga bez mijenjanja aktivnog — null ako je
  // sesija istekla (nalog ostaje u listi, ali traži ponovnu prijavu).
  async verify({ commit }, account) {
    if (!account.token) return null;
    try {
      const res = await userService.getCurrentUser(account.token);
      return res.data && res.data.id ? res.data : null;
    } catch (err) {
      if (err.response && err.response.status === 401) {
        commit("SET_ACCOUNTS", expireAccount(account.id));
        return null;
      }
      throw err;
    }
  },
  async switchTo({ dispatch }, account) {
    const user = await dispatch("verify", account);
    if (!user) return false;
    activateAccount(user, account.token);
    return true;
  },
  forget({ commit }, id) {
    commit("SET_ACCOUNTS", forgetAccount(id));
  },
  // Odjava aktivnog naloga — ako postoji drugi sačuvan nalog sa važećom
  // sesijom, prebacuje se na njega (kao Chrome kad se ukloni profil), inače
  // obična odjava (user/LOGOUT).
  async signOut({ commit, dispatch, rootState }) {
    const currentId = rootState.user.user && rootState.user.user.id;
    commit("SET_ACCOUNTS", forgetAccount(currentId));
    const candidates = readAccounts().filter((a) => a.token);
    for (const account of candidates) {
      try {
        if (await dispatch("switchTo", account)) return;
      } catch (e) {}
    }
    dispatch("user/LOGOUT", null, { root: true });
  },
  signOutAll({ commit, dispatch }) {
    commit("SET_ACCOUNTS", forgetAllAccounts());
    dispatch("user/LOGOUT", null, { root: true });
  },
};

/**
 * Pamti svaki nalog na koji se korisnik prijavi — svi tokovi (login, QR,
 * registracija, učitavanje sesije u router guard-u) završavaju sa
 * user/SAVE_USER, pa se lista puni na jednom mjestu. Svaka odjava
 * (user/LOGOUT, uključujući kantinu i recipient layout) briše nalog iz liste,
 * da njegov token ne ostane dostupan bez lozinke.
 */
export function accountsPlugin(store) {
  store.commit("accounts/SET_ACCOUNTS", readAccounts());

  let currentUserId = null;

  store.subscribe((mutation) => {
    if (mutation.type === "user/SAVE_USER") {
      const user = mutation.payload;
      if (!user || !user.id) return;
      currentUserId = user.id;
      stopAddingAccount();
      store.commit("accounts/SET_ACCOUNTS", rememberAccount(user, getActiveToken()));
    } else if (mutation.type === "user/LOGOUT") {
      if (currentUserId) {
        store.commit("accounts/SET_ACCOUNTS", forgetAccount(currentUserId));
      }
      currentUserId = null;
    }
  });

  // localStorage je zajednički za sve tabove: ako se u drugom tabu promijeni
  // aktivni nalog, ovaj tab bi prikazivao starog korisnika a slao zahtjeve kao
  // novi — zato se ponovo učitava.
  window.addEventListener("storage", (event) => {
    if (event.key === ACCOUNTS_KEY) {
      store.commit("accounts/SET_ACCOUNTS", readAccounts());
    } else if (event.key === TOKEN_KEY && event.oldValue !== event.newValue) {
      window.location.reload();
    }
  });
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
